/**
 * The campaign composer, and — once it has sent — its report.
 *
 * One page with a review rail rather than a wizard. Every field is visible at
 * once because amending a draft should not mean walking forward through steps
 * that are already answered.
 *
 * Four things here are imposed by the API rather than chosen, and each has a
 * failure mode if ignored:
 *
 *   Only published versions may be pinned. The fan-out refuses a draft, so
 *   offering one is a 422 waiting to happen at send time.
 *
 *   Scheduling does not pin the version — the campaign must already carry it.
 *   So the composer saves, then schedules, in that order.
 *
 *   Editing narrows to draft and scheduled. Past those the update endpoint
 *   reports not-found rather than a conflict, so the form goes read-only.
 *
 *   Cancelling does not unsend. Messages already queued still go out, and the
 *   confirmation has to say so.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { api, ApiError } from "../api/client";
import { useAsync } from "../hooks";
import { SkeletonRows } from "../components/Loading";
import { Metric, percentOf } from "../components/Metric";
import { Icon } from "../components/Sprites";
import { Status } from "../components/Status";
import { useSession } from "../session";
import { CampaignStatusChip } from "./Campaigns";
import type {
  AnalyticsSummary,
  Campaign,
  CampaignRecipient,
  ContactTag,
  CursorPage,
  Domain,
  Group,
  Organization,
  Template,
  TemplateVersion,
  UsageReport,
} from "../api/types";

/** How often a sending campaign is re-read. */
const POLL_MS = 4000;

interface Form {
  name: string;
  templateVersionId: string;
  fromAddr: string;
  groupIds: string[];
  tagId: string;
  /** Whether to send only to people who opened or clicked recently. */
  engaged: boolean;
  /** The window, as typed. A string so the field can be empty while editing. */
  engagedDays: string;
}

/** The window the engagement filter starts at when it is first turned on. */
const DEFAULT_ENGAGED_DAYS = 90;

export function CampaignComposer() {
  const { campaignId = "" } = useParams();
  const { orgId, can } = useSession();
  const navigate = useNavigate();
  const writable = can("campaigns:write");

  const campaign = useAsync<Campaign>(
    (signal) => api.get<Campaign>(`/v1/campaigns/${encodeURIComponent(campaignId)}`, { signal }),
    [orgId, campaignId],
  );

  const loaded = campaign.data;
  const editable = loaded?.status === "draft" || loaded?.status === "scheduled";

  const [form, setForm] = useState<Form | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!loaded) return;
    setForm({
      name: loaded.name,
      templateVersionId: loaded.template_version_id ?? "",
      fromAddr: loaded.from_addr ?? "",
      groupIds: loaded.audience?.group_ids ?? [],
      tagId: loaded.audience?.tag_id ?? "",
      engaged: loaded.audience?.engaged_within_days != null,
      engagedDays: String(loaded.audience?.engaged_within_days ?? DEFAULT_ENGAGED_DAYS),
    });
  }, [loaded?.id, loaded?.status]);

  // A sending campaign changes underneath the page, so it is re-read until it
  // stops. Nothing else polls: a draft only changes when someone edits it here.
  useEffect(() => {
    if (loaded?.status !== "sending") return;

    const timer = window.setInterval(() => campaign.reload(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [loaded?.status, campaign.reload]);

  if (campaign.error) {
    return (
      <>
        <div className="topbar">
          <h1 className="oc-headline-lg">Campaign</h1>
        </div>
        <div className="content">
          <div className="oc-panel oc-starfield" style={{ flex: 1 }}>
            <div className="oc-empty">
              <div className="oc-empty__title">Could not load this campaign</div>
              <div className="oc-empty__hint">{campaign.error.message}</div>
              <button type="button" className="oc-btn oc-btn--sm" onClick={() => navigate("/campaigns")}>
                Back to campaigns
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!loaded || !form) {
    return (
      <div className="content">
        <span className="oc-body-sm oc-text-secondary">Loading…</span>
      </div>
    );
  }

  const update = (patch: Partial<Form>) => setForm((current) => (current ? { ...current, ...patch } : current));

  /** Persists the form. Scheduling requires the version and sender to be stored. */
  const save = async (): Promise<boolean> => {
    setBusy("Saving");
    setError(null);
    try {
      await api.patch<Campaign>(`/v1/campaigns/${encodeURIComponent(campaignId)}`, {
        name: form.name.trim(),
        template_version_id: form.templateVersionId || undefined,
        from_addr: form.fromAddr.trim() || undefined,
        audience: {
          group_ids: form.groupIds,
          tag_id: form.tagId || null,
          channel: loaded.channel,
          // Sent as typed. A window out of range comes back as a field error
          // under the campaign rather than being clamped into one nobody chose.
          engaged_within_days: form.engaged ? Number(form.engagedDays) : null,
        },
      });
      campaign.reload();
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save the campaign.");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const send = async (sendAt: string | null) => {
    const when = sendAt ? `on ${new Date(sendAt).toLocaleString()}` : "now";
    if (
      !window.confirm(
        `Send "${form.name}" ${when}?\n\n` +
          "Only addresses with explicit consent on this channel are included. " +
          "Once the fan-out starts, messages already queued cannot be recalled.",
      )
    ) {
      return;
    }

    // Saved first, because scheduling only sets the status: the version and the
    // sender have to be on the row already or the API reports it unsendable.
    if (!(await save())) return;

    setBusy("Scheduling");
    setError(null);
    try {
      await api.post<Campaign>(
        `/v1/campaigns/${encodeURIComponent(campaignId)}/schedule`,
        sendAt ? { send_at: sendAt } : {},
      );
      campaign.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not schedule the campaign.");
    } finally {
      setBusy(null);
    }
  };

  const cancel = async () => {
    if (
      !window.confirm(
        `Cancel "${loaded.name}"?\n\n` +
          "This stops the fan-out queueing anything further. Messages already queued still " +
          "go out — unsending what is in the pipeline is not something this system can do.",
      )
    ) {
      return;
    }

    setBusy("Cancelling");
    try {
      await api.delete(`/v1/campaigns/${encodeURIComponent(campaignId)}`);
      campaign.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not cancel the campaign.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="topbar">
        <div className="row" style={{ gap: "var(--oc-space-3)", minWidth: 0 }}>
          <button
            type="button"
            className="oc-btn oc-btn--ghost oc-btn--icon oc-btn--sm"
            aria-label="Back to campaigns"
            onClick={() => navigate("/campaigns")}
          >
            <Icon name="chevron" size={14} className="icon--back" />
          </button>
          <h1 className="oc-headline-lg oc-truncate">{loaded.name}</h1>
          <CampaignStatusChip status={loaded.status} />
        </div>

        {writable && (loaded.status === "scheduled" || loaded.status === "sending") && (
          <button
            type="button"
            className="oc-btn oc-btn--danger oc-btn--sm"
            disabled={Boolean(busy)}
            onClick={() => void cancel()}
          >
            {busy === "Cancelling" ? "Cancelling…" : "Cancel campaign"}
          </button>
        )}
      </div>

      {error && (
        <div className="filters">
          <p className="field-error" role="alert">
            {error}
          </p>
        </div>
      )}

      <div className="content">
        <div className="composer">
          <div className="stack">
            {editable ? (
              <ComposerForm
                form={form}
                update={update}
                writable={writable}
                channel={loaded.channel}
              />
            ) : (
              <SentSummary campaign={loaded} />
            )}

            {loaded.status !== "draft" && <RecipientsTable campaignId={campaignId} />}
          </div>

          <div className="composer__rail">
            <ReviewRail
              campaign={loaded}
              form={form}
              editable={Boolean(editable)}
              writable={writable}
              busy={busy}
              onSave={() => void save()}
              onSend={(at) => void send(at)}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function ComposerForm({
  form,
  update,
  writable,
  channel,
}: {
  form: Form;
  update: (patch: Partial<Form>) => void;
  writable: boolean;
  channel: string;
}) {
  const { orgId } = useSession();

  const templates = useAsync<CursorPage<Template>>(
    (signal) => api.get<CursorPage<Template>>("/v1/templates", { query: { limit: 100 }, signal }),
    [orgId],
  );
  const groups = useAsync<CursorPage<Group>>(
    (signal) => api.get<CursorPage<Group>>("/v1/groups", { query: { limit: 100 }, signal }),
    [orgId],
  );
  const tags = useAsync<ContactTag[]>(
    (signal) => api.get<ContactTag[]>("/v1/tags", { signal }),
    [orgId],
  );
  const domains = useAsync<Domain[]>(
    (signal) => api.get<Domain[]>("/v1/domains", { signal }),
    [orgId],
  );

  // Published versions only. The fan-out refuses a draft, so a draft in this
  // list would be a campaign that fails at send time rather than at pick time.
  const sendable = useMemo(() => {
    const out: Array<{ version: TemplateVersion; template: Template }> = [];
    for (const template of templates.data?.data ?? []) {
      if (template.current_version?.published) {
        out.push({ version: template.current_version, template });
      }
    }
    return out;
  }, [templates.data]);

  const verified = (domains.data ?? []).filter((domain) => domain.verified);

  return (
    <div className="oc-panel">
      <div className="oc-panel__body stack" style={{ gap: "var(--oc-space-4)" }}>
        <div className="login__field">
          <label className="oc-label" htmlFor="campaign-name">
            Name
          </label>
          <input
            id="campaign-name"
            className="oc-input"
            value={form.name}
            readOnly={!writable}
            onChange={(event) => update({ name: event.target.value })}
          />
        </div>

        <div className="login__field">
          <label className="oc-label" htmlFor="campaign-template">
            Template version
          </label>
          <select
            id="campaign-template"
            className="oc-input"
            value={form.templateVersionId}
            disabled={!writable}
            onChange={(event) => update({ templateVersionId: event.target.value })}
          >
            <option value="">Choose a published version…</option>
            {sendable.map(({ version, template }) => (
              <option key={version.id} value={version.id}>
                {template.name} · v{version.version}
              </option>
            ))}
          </select>
          <span className="oc-body-sm oc-text-secondary">
            {sendable.length === 0
              ? "No template has a published version yet. A draft cannot be sent."
              : "Pinned when you schedule, so an edit on Thursday cannot change Friday's send."}
          </span>
        </div>

        <div className="login__field">
          <label className="oc-label" htmlFor="campaign-from">
            From
          </label>
          <input
            id="campaign-from"
            className="oc-input"
            placeholder="Acme Team <hello@mail.acme.dev>"
            value={form.fromAddr}
            readOnly={!writable}
            onChange={(event) => update({ fromAddr: event.target.value })}
          />
          <span className="oc-body-sm oc-text-secondary">
            {verified.length > 0 ? (
              <>Verified: {verified.map((domain) => domain.domain).join(", ")}</>
            ) : (
              <>
                No verified sending domain yet. Mail from an unverified domain lands in spam or is
                rejected.
              </>
            )}
          </span>
        </div>

        <div className="login__field">
          <span className="oc-label">Audience</span>
          {/* Checkboxes, not <select multiple>.
              A native multiple select needs ctrl-click to add a second item and
              silently clears the rest on a plain click — for the one control
              that decides who receives the mail, that is the wrong place to
              accept a footgun. Groups are few and already loaded with their
              counts, so a list is both clearer and less code. */}
          <GroupPicker
            groups={groups.data?.data ?? []}
            selected={form.groupIds}
            disabled={!writable}
            onChange={(groupIds) => update({ groupIds })}
          />

          <div className="row" style={{ gap: "var(--oc-space-2)", alignItems: "flex-start" }}>
            <select
              className="oc-input grow"
              aria-label="Tag"
              value={form.tagId}
              disabled={!writable}
              onChange={(event) => update({ tagId: event.target.value })}
            >
              <option value="">Any tag</option>
              {(tags.data ?? []).map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
            </select>
          </div>
          <span className="oc-body-sm oc-text-secondary">
            Both narrow the list; a contact must match each one you set. Only {channel} addresses
            with explicit consent are included — unknown consent is not permission, it is an
            address that arrived with nothing said about it.
          </span>

          {/* A separate label for the checkbox rather than one label around the
              whole row: a number field inside the checkbox's label would toggle
              the filter every time somebody clicked into it. */}
          <div className="row" style={{ gap: "var(--oc-space-2)", alignItems: "center", flexWrap: "wrap" }}>
            <input
              id="campaign-engaged"
              type="checkbox"
              checked={form.engaged}
              disabled={!writable}
              onChange={(event) => update({ engaged: event.target.checked })}
            />
            <label className="oc-body-sm" htmlFor="campaign-engaged">
              Only people who opened or clicked in the last
            </label>
            <input
              type="number"
              className="oc-input oc-input--sm"
              aria-label="Days"
              min={1}
              max={365}
              style={{ width: 80 }}
              value={form.engagedDays}
              disabled={!writable || !form.engaged}
              onChange={(event) => update({ engagedDays: event.target.value })}
            />
            <span className="oc-body-sm">days</span>
          </div>
          <span className="oc-body-sm oc-text-secondary">
            Use it to warm up a new domain, and to stop mailing people who stopped reading. Opens
            from Apple Mail do not count, because Apple loads every image whether the message is
            read or not. A list you just imported has no history here yet.
          </span>
        </div>
      </div>
    </div>
  );
}

/**
 * The review rail.
 *
 * The checklist is the same set of conditions the API enforces, stated before
 * the send rather than after it fails. Each item names what is missing, not that
 * something is.
 */
function ReviewRail({
  campaign,
  form,
  editable,
  writable,
  busy,
  onSave,
  onSend,
}: {
  campaign: Campaign;
  form: Form;
  editable: boolean;
  writable: boolean;
  busy: string | null;
  onSave: () => void;
  onSend: (sendAt: string | null) => void;
}) {
  const [at, setAt] = useState("");
  const { orgId } = useSession();

  // GET /v1/organization needs no capability, so everybody who can see this
  // rail can see why a campaign would be refused.
  const organization = useAsync<Organization>(
    (signal) => api.get<Organization>("/v1/organization", { signal }),
    [orgId],
  );
  // Every role holds usage:read; the warm-up line below needs it.
  const usage = useAsync<UsageReport>(
    (signal) => api.get<UsageReport>("/v1/usage", { signal }),
    [orgId],
  );

  const checks = [
    { met: Boolean(form.name.trim()), label: "Has a name", required: true },
    { met: Boolean(form.templateVersionId), label: "A published template version is chosen", required: true },
    { met: Boolean(form.fromAddr.trim()), label: "A sender address is set", required: true },
    {
      // Unknown until it loads, and not shown as missing meanwhile: a warning
      // that flashes on every page load is one people learn to ignore.
      met: organization.data ? Boolean(organization.data.postal_address.trim()) : true,
      label: "The organization has a postal address",
      note: "Add it in Settings, on the Organization tab. It is printed at the foot of every campaign.",
      required: true,
    },
    {
      met: form.groupIds.length > 0 || Boolean(form.tagId),
      label: "An audience is chosen",
      note: "Without one the campaign goes to every subscribed address on the channel.",
      required: false,
    },
  ];

  const ready = checks.filter((check) => check.required).every((check) => check.met);

  return (
    <>
      <div className="oc-panel">
        <div className="oc-panel__header">
          <span className="oc-label">Review</span>
        </div>
        <div className="oc-panel__body stack">
          <div className="checklist">
            {checks.map((check) => (
              <div key={check.label} className="checklist__item" data-met={check.met}>
                <Icon
                  name={check.met ? "check" : "warning"}
                  className={`checklist__mark ${check.met ? "oc-text-success" : "oc-text-warning"}`}
                />
                <span>
                  {check.label}
                  {!check.met && check.note && (
                    <span className="oc-text-secondary" style={{ display: "block" }}>
                      {check.note}
                    </span>
                  )}
                </span>
              </div>
            ))}
          </div>

          {campaign.status === "scheduled" && campaign.scheduled_at && (
            <p className="oc-body-sm" style={{ margin: 0 }}>
              Scheduled for{" "}
              <span className="oc-mono-sm">{new Date(campaign.scheduled_at).toLocaleString()}</span>
            </p>
          )}

          {/* Said here as well as on the Usage tab, because this is where the
              size of a send is decided, and a campaign larger than what is left
              today is refused whole. */}
          {editable && usage.data?.warmup && (
            <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
              Warm-up, day {usage.data.warmup.day} of {usage.data.warmup.days}: this organization
              can send {usage.data.warmup.remaining_today.toLocaleString()} more campaign emails
              today. A larger campaign is refused before anything is sent.
            </p>
          )}
        </div>
      </div>

      {editable && writable && (
        <div className="oc-panel">
          <div className="oc-panel__body stack">
            <button
              type="button"
              className="oc-btn oc-btn--sm"
              disabled={Boolean(busy)}
              onClick={onSave}
            >
              {busy === "Saving" ? "Saving…" : "Save draft"}
            </button>

            <button
              type="button"
              className="oc-btn oc-btn--primary"
              disabled={Boolean(busy) || !ready}
              onClick={() => onSend(null)}
            >
              {busy === "Scheduling" ? "Scheduling…" : "Send now"}
            </button>

            <div className="login__field">
              <label className="oc-label" htmlFor="campaign-when">
                Or schedule
              </label>
              <input
                id="campaign-when"
                className="oc-input"
                type="datetime-local"
                value={at}
                onChange={(event) => setAt(event.target.value)}
              />
              <button
                type="button"
                className="oc-btn oc-btn--sm"
                disabled={Boolean(busy) || !ready || !at}
                onClick={() => onSend(new Date(at).toISOString())}
              >
                Schedule
              </button>
            </div>
          </div>
        </div>
      )}

      {(campaign.status === "sending" || campaign.status === "sent" || campaign.status === "failed") && (
        <div className="oc-panel">
          <div className="oc-panel__header">
            <span className="oc-label">Result</span>
          </div>
          <div className="oc-panel__body">
            <div className="metrics">
              <Metric label="Queued" value={campaign.queued_count ?? 0} />
              <Metric label="Skipped" value={campaign.skipped_count ?? 0} />
              <Metric label="Total" value={campaign.total_recipients ?? 0} />
            </div>
            <p className="oc-body-sm oc-text-secondary" style={{ marginBottom: 0 }}>
              Skipped addresses are suppressed or were refused by the circuit breaker — the
              protections doing their job, recorded so it is visible that they did.
            </p>
          </div>

          {/* Fan-out above, outcomes below, and the split is the point: the
              three numbers above say how many this campaign tried to send, and
              answer nothing about whether any of it arrived. */}
          <div className="oc-panel__body" style={{ paddingTop: 0 }}>
            <CampaignOutcomes campaign={campaign} />
          </div>
        </div>
      )}
    </>
  );
}

/**
 * What actually happened to the mail, as opposed to how much of it was sent.
 *
 * Reads the analytics summary narrowed to this campaign — an endpoint that
 * already existed and that nothing called with a campaign_id until now. The
 * counts come from ClickHouse, keyed on the campaign_id that both the message
 * row and every event already carry.
 */
function CampaignOutcomes({ campaign }: { campaign: Campaign }) {
  const { orgId } = useSession();

  // The window opens when the send did and ends *now*, not when the fan-out
  // finished. Opens and clicks keep arriving for days after the last message
  // leaves; ending the range at finished_at would report engagement as zero for
  // every campaign and be most wrong for the most recent one.
  const from = campaign.started_at ?? campaign.created_at;
  const to = new Date().toISOString();

  const summary = useAsync<AnalyticsSummary>(
    (signal) =>
      api.get<AnalyticsSummary>("/v1/analytics/summary", {
        query: { from, to, campaign_id: campaign.id },
        signal,
      }),
    [orgId, campaign.id, from],
  );

  if (summary.loading) {
    return (
      <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
        Counting outcomes…
      </p>
    );
  }

  // Analytics is the one optional dependency a campaign screen can lose: the
  // core compose profile has no ClickHouse. Delivery is unaffected, so this
  // says what is missing rather than looking like the campaign failed.
  if (summary.error) {
    return (
      <p className="oc-body-sm oc-text-muted-aa" style={{ margin: 0 }}>
        Outcome counts need the analytics store. Delivery is unaffected, and the message
        log has every event for this campaign.
      </p>
    );
  }

  const totals = summary.data?.totals;
  if (!totals) return null;

  // Only the terminal outcomes link into the log, and the asymmetry is
  // deliberate. These counts are events; the log filters on a message's
  // *current* status, which is a single value that advances. A message
  // delivered and then opened has status `opened`, so "Delivered: 900" pointing
  // at status=delivered would open a list of however many were never opened —
  // a number that contradicts the one clicked on. Bounced, complained and
  // unsubscribed do not advance to anything, so for those the two agree, and
  // those are the ones worth drilling into anyway.
  const recipients = (status: string) =>
    `/messages?campaign_id=${campaign.id}&status=${status}`;

  return (
    <>
      <div className="metrics">
        <Metric label="Delivered" value={totals.delivered} note={percentOf(totals.delivered, totals.sent)} />
        {/* People, not events: each message once, a click counting as an open.
            The MPP-excluded figure stays beside it, as everywhere else, and so
            do the event totals, which say how much activity there was. */}
        <Metric
          label="Opened"
          value={totals.unique.opened}
          note={`${percentOf(totals.unique.opened, totals.delivered)} · ${totals.unique.opened_excluding_mpp.toLocaleString()} excluding MPP · ${totals.opens.total.toLocaleString()} opens`}
        />
        <Metric
          label="Clicked"
          value={totals.unique.clicked}
          note={`${percentOf(totals.unique.clicked, totals.delivered)} · ${totals.clicked.toLocaleString()} clicks`}
        />
        <Metric
          label="Bounced"
          value={totals.bounced}
          negative={totals.bounced > 0}
          to={recipients("bounced")}
        />
        <Metric
          label="Complained"
          value={totals.complained}
          negative={totals.complained > 0}
          to={recipients("complained")}
        />
        <Metric
          label="Unsubscribed"
          value={totals.unsubscribed}
          to={recipients("unsubscribed")}
        />
      </div>
      <p className="oc-body-sm oc-text-secondary" style={{ marginBottom: "var(--oc-space-3)" }}>
        Counted from the send onwards, so these keep moving after the fan-out finishes.
        Opens are attributed by user agent, which makes the MPP figure a floor rather than
        an exact count.
      </p>
      <Link className="oc-btn oc-btn--ghost oc-btn--sm" to={`/messages?campaign_id=${campaign.id}`}>
        <Icon name="search" size={12} />
        See every recipient in the message log
      </Link>
    </>
  );
}

/** What a campaign past editing shows instead of the form. */
function SentSummary({ campaign }: { campaign: Campaign }) {
  return (
    <>
      {campaign.failure && (
        <div className="oc-panel oc-panel--critical" role="alert">
          <div className="oc-panel__body">
            <span className="oc-label">The fan-out failed</span>
            <p className="oc-body-sm" style={{ marginBottom: 0 }}>
              {campaign.failure}
            </p>
          </div>
        </div>
      )}

      <div className="oc-panel">
        <div className="oc-panel__header">
          <span className="oc-label">Campaign</span>
        </div>
        <div className="oc-panel__body stack">
          <p className="oc-body-sm oc-text-secondary" style={{ margin: 0 }}>
            A campaign stops being editable once it starts. Changing the template halfway through
            would mean half the list got one message and half got another.
          </p>
          <Field label="Status" value={campaign.status} />
          {campaign.started_at && (
            <Field label="Started" value={new Date(campaign.started_at).toLocaleString()} />
          )}
          {campaign.finished_at && (
            <Field label="Finished" value={new Date(campaign.finished_at).toLocaleString()} />
          )}
          {campaign.from_addr && <Field label="From" value={campaign.from_addr} />}
        </div>
      </div>
    </>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="row row--between">
      <span className="oc-label">{label}</span>
      <span className="oc-mono-sm">{value}</span>
    </div>
  );
}

/**
 * Who the campaign actually reached.
 *
 * The materialised audience, recorded when the send ran — re-running the
 * audience query later gives a different answer than the one the campaign acted
 * on.
 *
 * This endpoint has no cursor and defaults to 200, so the footer says how many
 * are shown rather than pretending to page.
 */
function RecipientsTable({ campaignId }: { campaignId: string }) {
  const navigate = useNavigate();
  const limit = 200;

  const { data, error, loading, reload } = useAsync<CampaignRecipient[]>(
    (signal) =>
      api.get<CampaignRecipient[]>(
        `/v1/campaigns/${encodeURIComponent(campaignId)}/recipients`,
        { query: { limit }, signal },
      ),
    [campaignId],
  );

  const rows = data ?? [];

  return (
    <div className="oc-panel">
      <div className="oc-panel__header row--between">
        <span className="oc-label">Recipients</span>
        <button type="button" className="oc-btn oc-btn--ghost oc-btn--sm" onClick={reload}>
          <Icon name="refresh" size={12} />
          Refresh
        </button>
      </div>

      <div className="oc-table recipients-table">
        <div className="oc-table__head" role="row">
          <span>Address</span>
          <span>Status</span>
          <span>Reason</span>
          <span className="oc-table__cell--right">Message</span>
        </div>

        <div className="oc-table__body">
          {error && (
            <div className="oc-empty">
              <div className="oc-empty__title">Could not load recipients</div>
              <div className="oc-empty__hint">{error.message}</div>
            </div>
          )}
          {loading && rows.length === 0 && <SkeletonRows columns={4} />}

          {!error && rows.length === 0 && !loading && (
            <div className="oc-empty">
              <div className="oc-empty__title">No recipients recorded yet</div>
              <div className="oc-empty__hint">
                The audience is materialised when the send starts, so this fills in as the fan-out
                runs.
              </div>
            </div>
          )}
          {rows.map((recipient) => (
            <div key={recipient.id} className="oc-table__row" role="row">
              {recipient.address.startsWith("erased:") ? (
                // The address was removed by a GDPR erasure. The row stays so the
                // campaign's counts still add up; the marker is only unique.
                <span className="oc-text-secondary">Erased contact</span>
              ) : (
                <span className="oc-mono-sm oc-truncate">{recipient.address}</span>
              )}
              {/* The message's own status once there is one. `state` is the
                  fan-out's record and stays "queued" forever, which read as
                  mail waiting to go out long after it was delivered. */}
              {recipient.message_status ? (
                <span>
                  <Status status={recipient.message_status} />
                </span>
              ) : (
                <span className="oc-mono-sm">
                  <RecipientState state={recipient.state} />
                </span>
              )}
              <span className="oc-body-sm oc-text-secondary oc-truncate" title={recipient.reason ?? ""}>
                {recipient.reason ?? "—"}
              </span>
              <span className="oc-table__cell--right">
                {recipient.message_id ? (
                  <button
                    type="button"
                    className="oc-btn oc-btn--ghost oc-btn--sm"
                    onClick={() => navigate(`/messages/${recipient.message_id}`)}
                  >
                    Open
                  </button>
                ) : (
                  <span className="oc-text-muted oc-mono-sm">—</span>
                )}
              </span>
            </div>
          ))}
        </div>

        <div className="oc-table__footer">
          <span className="oc-mono-sm">
            {rows.length === 0
              ? "No recipients"
              : rows.length >= limit
                ? `First ${limit}`
                : `${rows.length} recipient${rows.length === 1 ? "" : "s"}`}
          </span>
        </div>
      </div>
    </div>
  );
}

function RecipientState({ state }: { state: CampaignRecipient["state"] }) {
  const tone: Record<CampaignRecipient["state"], string> = {
    pending: "oc-text-secondary",
    queued: "oc-text-success",
    skipped: "oc-text-warning",
    failed: "oc-text-danger",
  };
  return <span className={tone[state]}>{state}</span>;
}

/**
 * Which groups a campaign goes to.
 *
 * The audience is a union — anyone in any of the ticked groups — and someone in
 * two of them receives one message, not two. That is worth saying on screen
 * rather than leaving people to hope: the deduplication happens in the fan-out
 * query, and nobody can see a query.
 *
 * The total shown is a ceiling rather than the send size. Overlap between
 * groups is not known here without asking the server for the resolved audience,
 * and consent is applied at send time — so the honest thing is to name what the
 * number is, and let the campaign's own recipient list be the truth.
 */
function GroupPicker({
  groups,
  selected,
  disabled,
  onChange,
}: {
  groups: Group[];
  selected: string[];
  disabled: boolean;
  onChange: (groupIds: string[]) => void;
}) {
  if (groups.length === 0) {
    return (
      <span className="oc-body-sm oc-text-muted-aa">
        No groups yet. Without one this campaign goes to every subscribed address on
        the channel — create a group to narrow it.
      </span>
    );
  }

  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((g) => g !== id) : [...selected, id]);
  };

  const chosen = groups.filter((g) => selected.includes(g.id));
  const ceiling = chosen.reduce((sum, g) => sum + g.member_count, 0);

  return (
    <div className="stack" style={{ gap: "var(--oc-space-2)" }}>
      <div
        className="oc-panel"
        style={{ maxHeight: 180, overflowY: "auto", padding: "var(--oc-space-2)" }}
      >
        {groups.map((group) => (
          <label
            key={group.id}
            className="row"
            style={{
              gap: "var(--oc-space-2)",
              padding: "var(--oc-space-1) var(--oc-space-2)",
              cursor: disabled ? "default" : "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={selected.includes(group.id)}
              disabled={disabled}
              onChange={() => toggle(group.id)}
            />
            <span className="oc-body-sm grow oc-truncate">{group.name}</span>
            <span className="oc-mono-sm oc-text-secondary">
              {group.member_count.toLocaleString()}
            </span>
          </label>
        ))}
      </div>

      <span className="oc-body-sm oc-text-secondary">
        {chosen.length === 0 ? (
          <>No group selected — this reaches every subscribed address on the channel.</>
        ) : (
          <>
            {chosen.length === 1 ? "1 group" : `${chosen.length} groups`}, up to{" "}
            {ceiling.toLocaleString()} contacts. Anyone in more than one is counted twice
            here and mailed once; consent is applied when the send starts, so the real
            figure is lower.
          </>
        )}
      </span>
    </div>
  );
}
